import { useEffect, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import PasswordInput from "@/components/forms/PasswordInput";

const ROLE_OPTIONS = [
  { value: "all", labelFr: "Toutes", labelEn: "All" },
  { value: "benevole", labelFr: "Bénévoles", labelEn: "Volunteers" },
  { value: "organisateur", labelFr: "Organisateurs", labelEn: "Organisers" },
  { value: "artiste", labelFr: "Artistes", labelEn: "Artists" },
  { value: "participant", labelFr: "Participants", labelEn: "Participants" },
];

const ROLE_BADGE = {
  benevole: { bg: "rgba(83,128,72,0.12)", fg: "var(--moss)" },
  organisateur: { bg: "rgba(153,27,27,0.12)", fg: "var(--cinnabar)" },
  artiste: { bg: "rgba(212,175,55,0.18)", fg: "var(--gold)" },
  participant: { bg: "rgba(30,58,138,0.1)", fg: "var(--lapis)" },
};

const ROLE_LABEL_FR = {
  participant: "Participant",
  benevole: "Bénévole",
  organisateur: "Organisateur",
  artiste: "Artiste",
};

function catLabel(role, locale) {
  return ROLE_LABEL_FR[role] || role || "—";
}

function InviteForm({ onSent, meta, locale }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("organisateur");
  const [func, setFunc] = useState("");
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const availableFuncs = meta?.functions_by_role?.[role] || [];

  // Reset function when role changes
  useEffect(() => { setFunc(""); }, [role]);

  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    setFeedback(null);
    try {
      const payload = { email, name, role };
      if (func) payload.function = func;
      const { data } = await api.post("/admin/espace/invitations", payload);
      setFeedback({ type: "ok", msg: locale === "fr" ? "Invitation envoyée." : "Invitation sent.", url: data.invite_url });
      setEmail(""); setName(""); setFunc("");
      onSent?.();
    } catch (err) {
      setFeedback({ type: "err", msg: err?.response?.data?.detail || "Erreur" });
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={send} data-testid="admin-invite-form" className="admin-card space-y-3">
      <div className="font-display text-xl mb-2">
        {locale === "fr" ? "Inviter un membre" : "Invite a member"}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        <input data-testid="invite-email" required type="email" placeholder="email@exemple.fr"
               value={email} onChange={(e) => setEmail(e.target.value)} className="input-classic" />
        <input data-testid="invite-name" placeholder={locale === "fr" ? "Nom (optionnel)" : "Name (optional)"}
               value={name} onChange={(e) => setName(e.target.value)} className="input-classic" />
        <select data-testid="invite-role" value={role} onChange={(e) => setRole(e.target.value)} className="input-classic">
          <option value="organisateur">{locale === "fr" ? "Organisateur" : "Organiser"}</option>
          <option value="artiste">{locale === "fr" ? "Artiste" : "Artist"}</option>
          <option value="benevole">{locale === "fr" ? "Bénévole" : "Volunteer"}</option>
        </select>
        <select
          data-testid="invite-function"
          value={func}
          onChange={(e) => setFunc(e.target.value)}
          disabled={availableFuncs.length === 0}
          className="input-classic"
        >
          <option value="">
            {availableFuncs.length === 0
              ? (locale === "fr" ? "— (aucun rôle)" : "— (no role)")
              : (locale === "fr" ? "Rôle (optionnel)" : "Role (optional)")}
          </option>
          {availableFuncs.map((f) => (
            <option key={f} value={f}>{meta?.function_labels?.[f] || f}</option>
          ))}
        </select>
        <button data-testid="invite-submit" type="submit" disabled={sending} className="btn btn-primary">
          {sending ? "…" : (locale === "fr" ? "Envoyer" : "Send")}
        </button>
      </div>
      {feedback && (
        <div className="text-sm font-body" style={{ color: feedback.type === "ok" ? "var(--moss)" : "var(--cinnabar)" }}>
          {feedback.msg}
          {feedback.url && (
            <a href={feedback.url} target="_blank" rel="noreferrer" className="ml-2 underline" style={{ color: "var(--lapis)" }}>
              {locale === "fr" ? "Lien direct" : "Direct link"}
            </a>
          )}
        </div>
      )}
    </form>
  );
}

function MemberRow({ m, meta, locale, onUpdated, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const [pwModal, setPwModal] = useState(null); // { mode: "set"|"reset", value?, result? }
  const badge = ROLE_BADGE[m.role] || { bg: "rgba(0,0,0,0.06)", fg: "var(--ink-2)" };
  const availableFuncs = meta?.functions_by_role?.[m.role] || [];

  const patch = async (body) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/members/${m.id}`, body);
      onUpdated?.(data);
    } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(locale === "fr" ? `Supprimer définitivement ${m.email} ?` : `Permanently delete ${m.email}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/members/${m.id}`);
      onDeleted?.(m.id);
    } finally { setBusy(false); }
  };

  const resetPassword = async () => {
    const ok = window.confirm(
      locale === "fr"
        ? `Réinitialiser le mot de passe de ${m.email} ?\nUn mot de passe temporaire lui sera envoyé par email.`
        : `Reset password for ${m.email}?\nA temporary password will be emailed to them.`
    );
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/members/${m.id}/reset-password`);
      setPwModal({ mode: "reset", result: data });
    } catch (err) {
      window.alert(err?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const submitSet = async (newPwd) => {
    setBusy(true);
    try {
      await api.post(`/admin/members/${m.id}/set-password`, { new_password: newPwd });
      setPwModal({ mode: "set", result: { ok: true } });
    } catch (err) {
      window.alert(err?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <tr data-testid={`member-row-${m.id}`} className="border-b" style={{ borderColor: "var(--border)", opacity: m.active === false ? 0.55 : 1 }}>
      <td className="py-3 px-3">
        <div className="font-display text-base">{m.name || "—"}</div>
        <div className="font-body text-xs" style={{ color: "var(--muted)" }}>{m.email}</div>
      </td>
      <td className="py-3 px-3">
        <span className="inline-block px-2.5 py-0.5 font-caps text-[0.55rem]"
              style={{ background: badge.bg, color: badge.fg, letterSpacing: "0.18em" }}>
          {catLabel(m.role, locale).toUpperCase()}
        </span>
      </td>
      <td className="py-3 px-3 font-body text-sm" style={{ color: "var(--ink)" }}>
        {availableFuncs.length === 0 ? (
          <span style={{ color: "var(--muted)" }}>—</span>
        ) : (
          <select
            data-testid={`member-function-${m.id}`}
            value={m.function || ""}
            disabled={busy}
            onChange={(e) => patch({ function: e.target.value || null })}
            className="font-body text-sm px-2 py-1 border"
            style={{ borderColor: "var(--border)", background: "var(--parchment)" }}
          >
            <option value="">— {locale === "fr" ? "Aucun" : "None"}</option>
            {availableFuncs.map((f) => (
              <option key={f} value={f}>{meta?.function_labels?.[f] || f}</option>
            ))}
          </select>
        )}
      </td>
      <td className="py-3 px-3 font-body text-xs" style={{ color: "var(--muted)" }}>
        {m.country || "—"}{m.city ? ` · ${m.city}` : ""}
      </td>
      <td className="py-3 px-3 font-body text-xs" style={{ color: "var(--muted)" }}>
        {(m.created_at || "").slice(0, 10)}
      </td>
      <td className="py-3 px-3">
        <div className="flex flex-wrap gap-2 justify-end">
          <select data-testid={`member-role-${m.id}`} value={m.role} disabled={busy}
                  onChange={(e) => patch({ role: e.target.value })}
                  className="font-caps text-[0.6rem] px-2 py-1 border" style={{ borderColor: "var(--border)", background: "var(--parchment)" }}>
            <option value="participant">Participant</option>
            <option value="benevole">Bénévole</option>
            <option value="organisateur">Organisateur</option>
            <option value="artiste">Artiste</option>
          </select>
          <button data-testid={`member-toggle-${m.id}`} type="button" disabled={busy}
                  onClick={() => patch({ active: !m.active })}
                  className="font-caps text-[0.6rem] px-2.5 py-1 border"
                  style={{ borderColor: "var(--border)", background: "transparent", color: "var(--ink-2)", letterSpacing: "0.15em" }}>
            {m.active === false ? (locale === "fr" ? "ACTIVER" : "ACTIVATE") : (locale === "fr" ? "DÉSACT." : "DEACT.")}
          </button>
          <button data-testid={`member-reset-pw-${m.id}`} type="button" disabled={busy}
                  onClick={resetPassword}
                  className="font-caps text-[0.6rem] px-2.5 py-1 border"
                  style={{ borderColor: "var(--lapis)", background: "transparent", color: "var(--lapis)", letterSpacing: "0.15em" }}
                  title={locale === "fr" ? "Réinitialiser le mot de passe (envoi par email)" : "Reset password (email)"}>
            {locale === "fr" ? "RESET MDP" : "RESET PWD"}
          </button>
          <button data-testid={`member-set-pw-${m.id}`} type="button" disabled={busy}
                  onClick={() => setPwModal({ mode: "set", value: "" })}
                  className="font-caps text-[0.6rem] px-2.5 py-1 border"
                  style={{ borderColor: "var(--gold)", background: "transparent", color: "var(--gold)", letterSpacing: "0.15em" }}
                  title={locale === "fr" ? "Définir un mot de passe manuellement" : "Set password manually"}>
            {locale === "fr" ? "DÉFINIR MDP" : "SET PWD"}
          </button>
          <button data-testid={`member-delete-${m.id}`} type="button" disabled={busy}
                  onClick={remove}
                  className="font-caps text-[0.6rem] px-2.5 py-1 border"
                  style={{ borderColor: "var(--cinnabar)", background: "transparent", color: "var(--cinnabar)", letterSpacing: "0.15em" }}>
            {locale === "fr" ? "SUPPR." : "DEL."}
          </button>
        </div>
      </td>
      {pwModal && (
        <PasswordModal
          mode={pwModal.mode}
          result={pwModal.result}
          member={m}
          locale={locale}
          onSubmit={submitSet}
          onClose={() => setPwModal(null)}
        />
      )}
    </tr>
  );
}

function PasswordModal({ mode, result, member, locale, onSubmit, onClose }) {
  const [value, setValue] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    setErr("");
    if (value.length < 8) { setErr(locale === "fr" ? "8 caractères minimum." : "Min 8 characters."); return; }
    if (value !== confirm) { setErr(locale === "fr" ? "Les mots de passe ne correspondent pas." : "Passwords do not match."); return; }
    onSubmit(value);
  };

  return createPortal(
    <div data-testid="pw-modal-backdrop"
         onClick={onClose}
         className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: "rgba(20,16,12,0.6)" }}>
      <div onClick={(e) => e.stopPropagation()}
           data-testid="pw-modal"
           className="bg-white border max-w-md w-full p-6 space-y-4"
           style={{ borderColor: "var(--border)", boxShadow: "0 20px 40px rgba(0,0,0,0.3)" }}>
        <div className="font-caps text-[0.65rem]" style={{ color: "var(--gold)", letterSpacing: "0.25em" }}>
          {mode === "reset"
            ? (locale === "fr" ? "RÉINITIALISATION ENVOYÉE" : "RESET SENT")
            : (result ? (locale === "fr" ? "MOT DE PASSE DÉFINI" : "PASSWORD SET")
                      : (locale === "fr" ? "DÉFINIR UN MOT DE PASSE" : "SET A PASSWORD"))}
        </div>
        <h3 className="font-display text-2xl">{member.name || member.email}</h3>
        <p className="font-body text-xs" style={{ color: "var(--muted)" }}>{member.email}</p>

        {result ? (
          <div className="space-y-3">
            <p className="font-body text-sm" style={{ color: "var(--moss)" }}>
              {mode === "reset"
                ? (locale === "fr"
                    ? `✓ Un mot de passe temporaire a été envoyé à ${result.temp_password_sent_to || member.email}. Le membre devra le modifier à sa prochaine connexion.`
                    : `✓ A temporary password has been sent to ${result.temp_password_sent_to || member.email}. The member will be asked to change it at next login.`)
                : (locale === "fr"
                    ? `✓ Le mot de passe a été défini. Un email de notification a été envoyé au membre.`
                    : `✓ Password has been set. A notification email was sent to the member.`)}
            </p>
            <button data-testid="pw-modal-close" onClick={onClose} className="btn btn-primary w-full">
              {locale === "fr" ? "Fermer" : "Close"}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <label className="block">
              <span className="label-caps">{locale === "fr" ? "NOUVEAU MOT DE PASSE" : "NEW PASSWORD"}</span>
              <div className="mt-1">
                <PasswordInput testId="pw-modal-new" required minLength={8} value={value}
                               onChange={(e) => setValue(e.target.value)} autoComplete="new-password" />
              </div>
            </label>
            <label className="block">
              <span className="label-caps">{locale === "fr" ? "CONFIRMER" : "CONFIRM"}</span>
              <div className="mt-1">
                <PasswordInput testId="pw-modal-confirm" required minLength={8} value={confirm}
                               onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
              </div>
            </label>
            <p className="font-body text-xs italic" style={{ color: "var(--muted)" }}>
              {locale === "fr"
                ? "À utiliser uniquement si vous communiquez le mot de passe directement au membre (téléphone, en personne). Un email de notification lui sera envoyé."
                : "Use only when sharing the password directly (phone, in person). The member will receive a notification email."}
            </p>
            {err && <p className="font-body text-sm" style={{ color: "var(--cinnabar)" }}>{err}</p>}
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="btn btn-outline" style={{ flex: 1 }}>
                {locale === "fr" ? "Annuler" : "Cancel"}
              </button>
              <button type="submit" data-testid="pw-modal-submit" className="btn btn-primary" style={{ flex: 1 }}>
                {locale === "fr" ? "Valider" : "Save"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body
  );
}

function InvitationsList({ items, meta, onDeleted, locale }) {
  if (!items || items.length === 0) {
    return (
      <div className="admin-card text-center font-body text-sm" style={{ color: "var(--muted)" }}>
        {locale === "fr" ? "Aucune invitation en attente." : "No pending invitations."}
      </div>
    );
  }
  const remove = async (id) => {
    if (!window.confirm(locale === "fr" ? "Supprimer cette invitation ?" : "Delete this invitation?")) return;
    await api.delete(`/admin/espace/invitations/${id}`);
    onDeleted?.(id);
  };
  const resend = async (id) => {
    try {
      await api.post(`/admin/espace/invitations/${id}/resend`);
      window.alert(locale === "fr"
        ? "Invitation renvoyée avec succès (nouveau lien valable 48h)."
        : "Invitation re-sent successfully (new link valid for 48h).");
      onDeleted?.(id); // triggers reload
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Erreur");
    }
  };
  return (
    <div className="admin-card">
      <div className="font-display text-xl mb-3">{locale === "fr" ? "Invitations en cours" : "Pending invitations"}</div>
      <ul className="divide-y" style={{ borderColor: "var(--border)" }}>
        {items.map((i) => {
          const funcLabel = i.function ? (meta?.function_labels?.[i.function] || i.function) : null;
          return (
            <li key={i.id} data-testid={`invite-row-${i.id}`} className="py-2 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-body text-sm">{i.name || i.email}</div>
                <div className="font-body text-xs" style={{ color: "var(--muted)" }}>
                  {i.email} · {catLabel(i.role, locale)}{funcLabel ? ` · ${funcLabel}` : ""} ·{" "}
                  {i.accepted ? (locale === "fr" ? "Acceptée" : "Accepted") : (locale === "fr" ? "En attente" : "Pending")}
                  {i.resent_at && <span> · {locale === "fr" ? "Renvoyée le" : "Re-sent on"} {new Date(i.resent_at).toLocaleDateString(locale)}</span>}
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => resend(i.id)} data-testid={`invite-resend-${i.id}`}
                        className="font-caps text-[0.55rem] px-2 py-1 border"
                        style={{ borderColor: "var(--gold)", color: "var(--gold)", letterSpacing: "0.18em" }}>
                  {locale === "fr" ? "↻ RENVOYER" : "↻ RESEND"}
                </button>
                <button onClick={() => remove(i.id)} data-testid={`invite-delete-${i.id}`}
                        className="font-caps text-[0.55rem] px-2 py-1 border"
                        style={{ borderColor: "var(--cinnabar)", color: "var(--cinnabar)", letterSpacing: "0.18em" }}>
                  {locale === "fr" ? "RETIRER" : "REMOVE"}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function MembersTab({ locale }) {
  const [filter, setFilter] = useState("all");
  const [members, setMembers] = useState([]);
  const [counts, setCounts] = useState({});
  const [invitations, setInvitations] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);

  const headers = useMemo(() => ({
    cat: locale === "fr" ? "CATÉGORIE" : "CATEGORY",
    role: locale === "fr" ? "RÔLE" : "ROLE",
    member: locale === "fr" ? "MEMBRE" : "MEMBER",
    loc: locale === "fr" ? "LIEU" : "LOCATION",
    joined: locale === "fr" ? "INSCRIT" : "JOINED",
    actions: "ACTIONS",
  }), [locale]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const role = filter === "all" ? undefined : filter;
      const [m, inv, meta] = await Promise.all([
        api.get("/admin/members", { params: role ? { role } : {} }),
        api.get("/admin/espace/invitations"),
        api.get("/admin/members/meta"),
      ]);
      setMembers(m.data.items || []);
      setCounts(m.data.counts || {});
      setInvitations(inv.data || []);
      setMeta(meta.data);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-members-tab" className="space-y-6">
      <InviteForm onSent={load} meta={meta} locale={locale} />

      <InvitationsList items={invitations.filter((i) => !i.accepted)} meta={meta} onDeleted={load} locale={locale} />

      <VolunteerApplicationsCard locale={locale} onChanged={load} />

      <div className="admin-card">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div>
            <div className="font-display text-xl">{locale === "fr" ? "Membres" : "Members"}</div>
            <div className="font-body text-xs" style={{ color: "var(--muted)" }}>
              {locale === "fr" ? "Total" : "Total"}: <strong>{counts.all || 0}</strong>
              {" · "}{locale === "fr" ? "Bénévoles" : "Volunteers"}: {counts.benevole || 0}
              {" · "}{locale === "fr" ? "Organisateurs" : "Organisers"}: {counts.organisateur || 0}
              {" · "}{locale === "fr" ? "Artistes" : "Artists"}: {counts.artiste || 0}
              {" · "}{locale === "fr" ? "Participants" : "Participants"}: {counts.participant || 0}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {ROLE_OPTIONS.map((o) => (
              <button
                key={o.value}
                data-testid={`members-filter-${o.value}`}
                onClick={() => setFilter(o.value)}
                className="font-caps text-[0.6rem] px-3 py-1.5 border"
                style={{
                  background: filter === o.value ? "var(--parchment-2)" : "transparent",
                  borderColor: filter === o.value ? "var(--cinnabar)" : "var(--border)",
                  color: filter === o.value ? "var(--cinnabar)" : "var(--ink-2)",
                  letterSpacing: "0.18em",
                }}
              >
                {locale === "en" ? o.labelEn : o.labelFr}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 font-body text-sm" style={{ color: "var(--muted)" }}>…</div>
        ) : members.length === 0 ? (
          <div className="text-center py-12 font-body text-sm" style={{ color: "var(--muted)" }}>
            {locale === "fr" ? "Aucun membre dans cette catégorie." : "No member in this category."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full font-body text-sm">
              <thead>
                <tr className="border-b" style={{ borderColor: "var(--border)" }}>
                  <th className="text-left py-2 px-3 font-caps text-[0.6rem]" style={{ color: "var(--muted)", letterSpacing: "0.18em" }}>{headers.member}</th>
                  <th className="text-left py-2 px-3 font-caps text-[0.6rem]" style={{ color: "var(--muted)", letterSpacing: "0.18em" }}>{headers.cat}</th>
                  <th className="text-left py-2 px-3 font-caps text-[0.6rem]" style={{ color: "var(--muted)", letterSpacing: "0.18em" }}>{headers.role}</th>
                  <th className="text-left py-2 px-3 font-caps text-[0.6rem]" style={{ color: "var(--muted)", letterSpacing: "0.18em" }}>{headers.loc}</th>
                  <th className="text-left py-2 px-3 font-caps text-[0.6rem]" style={{ color: "var(--muted)", letterSpacing: "0.18em" }}>{headers.joined}</th>
                  <th className="text-right py-2 px-3 font-caps text-[0.6rem]" style={{ color: "var(--muted)", letterSpacing: "0.18em" }}>{headers.actions}</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <MemberRow key={m.id} m={m} meta={meta} locale={locale} onUpdated={load} onDeleted={load} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const INTEREST_LABELS_FR = {
  accueil: "Accueil", logistique: "Logistique", restauration: "Restauration",
  communication: "Communication", billetterie: "Billetterie", securite: "Sécurité",
  transport: "Transport", artistique: "Artistique", autre: "Autre",
};

function VolunteerApplicationsCard({ locale, onChanged }) {
  const isFr = locale === "fr";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/volunteer/applications", {
        params: { status: "pending_review" },
      });
      setItems(data.items || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (m) => {
    setBusyId(m.id);
    try {
      await api.post(`/admin/volunteer/applications/${m.id}/approve`);
      await load();
      onChanged?.();
    } finally { setBusyId(null); }
  };

  const reject = async (m) => {
    const reason = window.prompt(
      isFr ? "Raison du refus (optionnel) — visible uniquement en interne :" : "Reason for refusal (optional, internal):",
      ""
    );
    if (reason === null) return; // cancelled
    setBusyId(m.id);
    try {
      await api.post(`/admin/volunteer/applications/${m.id}/reject`, { reason });
      await load();
      onChanged?.();
    } finally { setBusyId(null); }
  };

  if (loading && items.length === 0) return null;
  if (!loading && items.length === 0) return null;

  return (
    <div className="admin-card" data-testid="volunteer-applications-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-display text-xl">
            ⚑ {isFr ? "Candidatures bénévoles en attente" : "Pending volunteer applications"}
            <span className="ml-3 font-caps text-[0.7rem]" style={{ color: "var(--cinnabar)" }}>
              {items.length}
            </span>
          </div>
          <div className="font-body text-xs" style={{ color: "var(--muted)" }}>
            {isFr
              ? "Approuvez ou refusez : un email est envoyé automatiquement au candidat."
              : "Approve or reject: an email is automatically sent to the applicant."}
          </div>
        </div>
        <button
          className="btn btn-outline btn-xs"
          onClick={() => setCollapsed((v) => !v)}
          data-testid="vol-apps-toggle"
        >
          {collapsed ? (isFr ? "Afficher" : "Show") : (isFr ? "Réduire" : "Collapse")}
        </button>
      </div>

      {!collapsed && (
        <div className="space-y-3">
          {items.map((m) => {
            const app = m.volunteer_application || {};
            const interests = app.interests || [];
            return (
              <div
                key={m.id}
                data-testid={`vol-app-${m.id}`}
                className="border bg-white p-4"
                style={{ borderColor: "var(--border)" }}
              >
                <div className="flex flex-wrap items-start gap-3">
                  <div className="flex-1 min-w-[240px]">
                    <div className="font-display text-lg">{m.name}</div>
                    <div className="font-body text-sm" style={{ color: "var(--muted)" }}>
                      <a href={`mailto:${m.email}`} className="link-underline">{m.email}</a>
                      {m.phone && <span> · {m.phone}</span>}
                      {(m.city || m.country) && <span> · {[m.city, m.country].filter(Boolean).join(", ")}</span>}
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {interests.map((i) => (
                        <span
                          key={i}
                          className="font-caps text-[0.55rem] px-2 py-0.5 border"
                          style={{
                            borderColor: "var(--border)",
                            color: "var(--ink-2)",
                            letterSpacing: "0.18em",
                          }}
                        >
                          {INTEREST_LABELS_FR[i] || i}
                        </span>
                      ))}
                    </div>
                    {app.motivation && (
                      <blockquote
                        className="mt-3 pl-4 font-body text-sm italic"
                        style={{ borderLeft: "3px solid var(--cinnabar)", color: "var(--ink-2)" }}
                      >
                        {app.motivation}
                      </blockquote>
                    )}
                    {app.submitted_at && (
                      <div className="font-caps text-[0.55rem] mt-2" style={{ color: "var(--muted)" }}>
                        {isFr ? "REÇU LE" : "RECEIVED"} {new Date(app.submitted_at).toLocaleDateString(locale)}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <button
                      className="btn btn-primary btn-xs"
                      onClick={() => approve(m)}
                      disabled={busyId === m.id}
                      data-testid={`vol-approve-${m.id}`}
                    >
                      {isFr ? "✓ Approuver" : "✓ Approve"}
                    </button>
                    <button
                      className="btn btn-outline btn-xs"
                      onClick={() => reject(m)}
                      disabled={busyId === m.id}
                      style={{ color: "var(--cinnabar)", borderColor: "var(--cinnabar)" }}
                      data-testid={`vol-reject-${m.id}`}
                    >
                      {isFr ? "✕ Refuser" : "✕ Reject"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

